import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    MdListAlt,
    MdAccessTime,
    MdCheck,
    MdClose,
    MdEditNote,
    MdNotes,
    MdContentCut,
    MdEvent,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdWarning
} from 'react-icons/md';

import { appointmentsAPI } from 'services/api';

// --- YARDIMCI FONKSİYONLAR (WORK-LOGIC BİREBİR) ---
const getLocalDateString = (isoString) => {
    if (!isoString) return "";
    return isoString.split("T")[0];
};

const getDayName = (dateString) => {
    const days = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
    const date = new Date(dateString.replace(/-/g, "/"));
    return days[date.getDay()];
};

const formatDateWithDay = (isoDateString) => {
    const localDateStr = getLocalDateString(isoDateString);
    if (!localDateStr) return "Geçersiz Tarih";
    const dayName = getDayName(localDateStr);
    const [year, month, day] = localDateStr.split("-");
    return `${day}.${month}.${year} (${dayName})`;
};

const getTodayStr = () => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = String(today.getMonth() + 1).padStart(2, "0");
    const todayDay = String(today.getDate()).padStart(2, "0");
    return `${todayYear}-${todayMonth}-${todayDay}`;
};

// --- SKELETON LOADER ---
const UpcomingSkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse mt-4">
        <div className="flex gap-2 mb-2">
            <div className="h-12 w-1/3 rounded-xl bg-gray-200 dark:bg-navy-700" />
            <div className="h-12 w-1/3 rounded-xl bg-gray-200 dark:bg-navy-700" />
            <div className="h-12 w-1/3 rounded-xl bg-gray-200 dark:bg-navy-700" />
        </div>
        {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
        ))}
    </div>
);

// --- ANA BİLEŞEN ---
const StaffUpcomingAppointments = () => {
    // --- STATELER ---
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("today");
    const [processingAction, setProcessingAction] = useState({ id: null, type: null }); // Buton loader için

    // Modal Stateleri
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [currentNote, setCurrentNote] = useState("");
    const [noteSaving, setNoteSaving] = useState(false);

    // Sayfalama (Pagination)
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    const todayStr = getTodayStr();
    const now = new Date();

    // --- KATEGORİZASYON (WORK-LOGIC BİREBİR) ---
    const categorized = useMemo(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tmrwYear = tomorrow.getFullYear();
        const tmrwMonth = String(tomorrow.getMonth() + 1).padStart(2, "0");
        const tmrwDay = String(tomorrow.getDate()).padStart(2, "0");
        const tomorrowStr = `${tmrwYear}-${tmrwMonth}-${tmrwDay}`;

        const todayAppts = appointments.filter((app) => getLocalDateString(app.appointment_date) === todayStr);
        const tomorrowAppts = appointments.filter((app) => getLocalDateString(app.appointment_date) === tomorrowStr);
        const futureAppts = appointments.filter((app) => getLocalDateString(app.appointment_date) > tomorrowStr);

        return { todayAppts, tomorrowAppts, futureAppts };
    }, [appointments, todayStr]);

    // --- API VERİ ÇEKME ---
    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const response = await appointmentsAPI.getMyAppointments();

            if (response.success) {
                const filteredAppts = (response.appointments || [])
                    .filter((app) => app.status === "approved")
                    .sort((a, b) => {
                        const dateA = getLocalDateString(a.appointment_date);
                        const dateB = getLocalDateString(b.appointment_date);
                        if (dateA === dateB) {
                            return a.appointment_time.localeCompare(b.appointment_time);
                        }
                        return dateA.localeCompare(dateB);
                    });
                setAppointments(filteredAppts);
            }
        } catch (error) {
            console.error("Randevular yüklenirken hata:", error);
            toast.error("Randevular yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, []);

    // --- HANDLERS (İŞLEMLER) ---
    const handleStatusChange = async (appointmentId, newStatus) => {
        try {
            setProcessingAction({ id: appointmentId, type: newStatus });
            const response = await appointmentsAPI.updateStatus(appointmentId, newStatus);

            if (response.success) {
                setAppointments(prev => prev.filter((app) => app.id !== appointmentId));
                if (newStatus === "completed") {
                    toast.success("Randevu başarıyla tamamlandı.");
                } else if (newStatus === "cancelled") {
                    toast.info("Randevu iptal edildi.");
                }
            }
        } catch (error) {
            console.error("Durum güncellenirken hata:", error);
            toast.error("İşlem sırasında bir hata oluştu.");
        } finally {
            setProcessingAction({ id: null, type: null });
        }
    };

    const handleOpenModal = (appointment) => {
        setEditingAppointment(appointment);
        setCurrentNote(appointment.note || "");
        setIsModalVisible(true);
    };

    const handleSaveNote = async () => {
        try {
            setNoteSaving(true);
            const response = await appointmentsAPI.update(editingAppointment.id, { note: currentNote });

            if (response.success) {
                setAppointments(prev => prev.map((app) => (app.id === editingAppointment.id ? { ...app, note: currentNote } : app)));
                setIsModalVisible(false);
                toast.success("Not başarıyla güncellendi.");
            }
        } catch (error) {
            console.error("Not kaydedilirken hata:", error);
            toast.error("Not kaydedilirken bir hata oluştu.");
        } finally {
            setNoteSaving(false);
        }
    };

    // --- SAYFALAMA HESAPLARI ---
    const activeList = activeTab === "today" ? categorized.todayAppts : activeTab === "tomorrow" ? categorized.tomorrowAppts : categorized.futureAppts;
    const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
    const currentPaginatedData = activeList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // Sekme değiştiğinde sayfayı sıfırla
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);

    // --- RENDER YARDIMCILARI ---
    const renderList = () => {
        if (activeList.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-16 opacity-60">
                    <MdEvent className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-1">Bu tarihte bekleyen randevunuz yok.</h5>
                    <p className="text-sm font-medium text-gray-500 text-center">Harika! Biraz dinlenme zamanı.</p>
                </div>
            );
        }

        return (
            <div className="p-4 md:p-0">
                {/* --- MASAÜSTÜ GÖRÜNÜM (TABLO) --- */}
                <div className="hidden md:block overflow-x-auto p-2">
                    <table className="w-full text-left border-collapse">
                        <thead>
                        <tr>
                            <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Müşteri</th>
                            <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Hizmet</th>
                            <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Tarih & Saat</th>
                            <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10 text-center">Notlar</th>
                            <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10 text-right">İşlemler</th>
                        </tr>
                        </thead>
                        <tbody>
                        {currentPaginatedData.map((app) => {
                            const appDateStr = getLocalDateString(app.appointment_date);
                            const appTimeStr = app.appointment_time || "00:00";
                            const [year, month, day] = appDateStr.split("-").map(Number);
                            const [hour, minute] = appTimeStr.split(":").map(Number);
                            const appDateTime = new Date(year, month - 1, day, hour, minute);

                            // Saati gelmeyen randevuyu tamamlama koruması (Work-Logic korundu)
                            const isFutureAppointment = appDateTime > now;

                            const isCompleting = processingAction.id === app.id && processingAction.type === 'completed';
                            const isCancelling = processingAction.id === app.id && processingAction.type === 'cancelled';
                            const isAnyProcessing = processingAction.id !== null;

                            return (
                                <tr key={app.id} className="hover:bg-gray-50/50 dark:hover:bg-navy-900/50 transition-colors border-b border-gray-50 dark:border-white/5 last:border-none">
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-navy-700 dark:text-white">
                            {app.customer ? `${app.customer.name} ${app.customer.surname}` : "Bilinmiyor"}
                          </span>
                                                {app.customer?.tag && (
                                                    <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider">
                              {app.customer.tag}
                            </span>
                                                )}
                                            </div>
                                            {app.customer?.phone && <span className="text-xs font-medium text-gray-500 mt-1">{app.customer.phone}</span>}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-col">
                        <span className="flex items-center gap-1.5 text-sm font-bold text-gray-600 dark:text-gray-300">
                          <MdContentCut className="text-gray-400" /> {app.service ? app.service.name : "Bilinmiyor"}
                        </span>
                                            {app.service?.duration && <span className="text-[10px] font-medium text-gray-400 mt-1 ml-5">{app.service.duration} dk</span>}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            {activeTab !== "today" && <span className="text-xs font-bold text-navy-700 dark:text-white mb-1">{formatDateWithDay(app.appointment_date)}</span>}
                                            <span className="flex items-center gap-1.5 text-sm font-black text-brand-500">
                          <MdAccessTime /> {app.appointment_time?.substring(0,5)}
                        </span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-center">
                                        <button
                                            onClick={() => handleOpenModal(app)}
                                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${app.note ? 'bg-blue-50 text-blue-500 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20' : 'bg-gray-50 text-gray-400 hover:bg-gray-100 dark:bg-navy-800 dark:text-gray-500 dark:hover:bg-navy-700'}`}
                                            title={app.note ? "Notu Görüntüle / Düzenle" : "Not Ekle"}
                                        >
                                            {app.note ? <MdNotes className="text-sm" /> : <MdEditNote className="text-sm" />}
                                            {app.note ? 'Not Oku' : 'Not Ekle'}
                                        </button>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleStatusChange(app.id, "completed")}
                                                disabled={isFutureAppointment || isAnyProcessing}
                                                title={isFutureAppointment ? "Randevu saati henüz gelmedi" : "Tamamlandı İşaretle"}
                                                className={`flex h-9 items-center justify-center gap-1.5 px-3 rounded-xl text-xs font-bold shadow-sm transition-all active:scale-95 ${isFutureAppointment ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-navy-800 dark:text-gray-600' : 'bg-green-500 text-white shadow-green-500/20 hover:bg-green-600 disabled:opacity-50'}`}
                                            >
                                                {isCompleting ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdCheck className="text-sm" /> Tamamlandı</>}
                                            </button>

                                            <button
                                                onClick={() => {
                                                    if(window.confirm("Bu randevuyu iptal etmek istediğinize emin misiniz?")) handleStatusChange(app.id, "cancelled");
                                                }}
                                                disabled={isAnyProcessing}
                                                title="İptal Et"
                                                className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-all active:scale-95 disabled:opacity-50"
                                            >
                                                {isCancelling ? <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <MdClose className="text-lg" />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {/* --- MOBİL GÖRÜNÜM (KARTLAR) --- */}
                <div className="flex flex-col gap-4 md:hidden">
                    {currentPaginatedData.map((app) => {
                        const appDateStr = formatDateWithDay(app.appointment_date);
                        const isFutureAppointment = (() => {
                            const ds = getLocalDateString(app.appointment_date);
                            const ts = app.appointment_time || "00:00";
                            const [y, m, d] = ds.split("-").map(Number);
                            const [hh, mm] = ts.split(":").map(Number);
                            return new Date(y, m - 1, d, hh, mm) > now;
                        })();

                        const isCompleting = processingAction.id === app.id && processingAction.type === 'completed';
                        const isCancelling = processingAction.id === app.id && processingAction.type === 'cancelled';
                        const isAnyProcessing = processingAction.id !== null;

                        return (
                            <div key={app.id} className="flex flex-col p-5 rounded-[20px] bg-white dark:bg-navy-900 border border-gray-100 dark:border-white/5 shadow-sm">

                                {/* Müşteri Üst Bilgisi */}
                                <div className="flex justify-between items-start mb-3 border-b border-gray-100 dark:border-white/5 pb-3">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 mb-1">
                      <span className="text-base font-bold text-navy-700 dark:text-white leading-tight">
                        {app.customer ? `${app.customer.name} ${app.customer.surname}` : "Bilinmiyor"}
                      </span>
                                            {app.customer?.tag && (
                                                <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider">
                          {app.customer.tag}
                        </span>
                                            )}
                                        </div>
                                        {app.customer?.phone && <span className="text-xs font-medium text-gray-500">{app.customer.phone}</span>}
                                    </div>
                                </div>

                                {/* Hizmet ve Zaman Bilgileri */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Hizmet</span>
                                        <span className="text-sm font-bold text-navy-700 dark:text-white">{app.service ? app.service.name : "Bilinmiyor"}</span>
                                        {app.service?.duration && <span className="text-xs font-medium text-gray-500">{app.service.duration} dk</span>}
                                    </div>
                                    <div className="flex flex-col items-end text-right">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Zaman</span>
                                        {activeTab !== "today" && <span className="text-xs font-medium text-gray-500">{appDateStr}</span>}
                                        <span className="text-base font-black text-brand-500">{app.appointment_time?.substring(0,5)}</span>
                                    </div>
                                </div>

                                {/* Butonlar Grubu */}
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-white/5 mt-auto">
                                    <button
                                        onClick={() => handleOpenModal(app)}
                                        className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-bold transition-all ${app.note ? 'bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400' : 'bg-gray-50 text-gray-500 dark:bg-navy-800 dark:text-gray-400'}`}
                                    >
                                        {app.note ? <MdNotes className="text-sm"/> : <MdEditNote className="text-sm"/>} {app.note ? 'Not Oku' : 'Not Ekle'}
                                    </button>

                                    <button
                                        onClick={() => handleStatusChange(app.id, "completed")}
                                        disabled={isFutureAppointment || isAnyProcessing}
                                        className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-bold transition-all ${isFutureAppointment ? 'bg-gray-100 text-gray-400 dark:bg-navy-800 dark:text-gray-600 cursor-not-allowed' : 'bg-green-500 text-white shadow-md shadow-green-500/20 active:scale-95 disabled:opacity-50'}`}
                                    >
                                        {isCompleting ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdCheck className="text-sm" /> Tamamla</>}
                                    </button>

                                    <button
                                        onClick={() => {
                                            if(window.confirm("İptal etmek istediğinize emin misiniz?")) handleStatusChange(app.id, "cancelled");
                                        }}
                                        disabled={isAnyProcessing}
                                        className="flex-none flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {isCancelling ? <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <MdClose className="text-lg" />}
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">
            <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

            {/* 1. ÜST BAŞLIK */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-lg shadow-brand-500/30 flex-none">
                        <MdListAlt className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Yaklaşan Randevular</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Onaylanmış ve yönetilmeyi bekleyen randevularınız.
                        </p>
                    </div>
                </div>
            </div>

            {/* 2. SEKMELER (TABS) & İÇERİK KARTI */}
            <div className="bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">

                {/* HorizonUI Tabs */}
                <div className="flex items-center overflow-x-auto border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20 custom-sidebar-scroll">
                    <button
                        onClick={() => setActiveTab('today')}
                        className={`flex-none px-6 py-4 text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'today' ? 'border-b-2 border-brand-500 text-brand-500 bg-white dark:bg-navy-800' : 'text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white'}`}
                    >
                        Bugün ({categorized.todayAppts.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('tomorrow')}
                        className={`flex-none px-6 py-4 text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'tomorrow' ? 'border-b-2 border-brand-500 text-brand-500 bg-white dark:bg-navy-800' : 'text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white'}`}
                    >
                        Yarın ({categorized.tomorrowAppts.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('future')}
                        className={`flex-none px-6 py-4 text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'future' ? 'border-b-2 border-brand-500 text-brand-500 bg-white dark:bg-navy-800' : 'text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white'}`}
                    >
                        İleri Tarihler ({categorized.futureAppts.length})
                    </button>
                </div>

                {/* Tab İçeriği */}
                <div className="p-0">
                    {loading ? <UpcomingSkeleton /> : renderList()}
                </div>

                {/* 3. SAYFALAMA (PAGINATION) */}
                {!loading && totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between p-6 border-t border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20 gap-4">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              <span className="font-bold text-navy-700 dark:text-white">{activeList.length}</span> kayıttan <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, activeList.length)}</span> gösteriliyor.
            </span>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                            >
                                <MdKeyboardArrowLeft className="text-2xl" />
                            </button>

                            <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-brand-500 text-white text-sm font-bold shadow-md px-3">
                                {currentPage}
                            </div>

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                            >
                                <MdKeyboardArrowRight className="text-2xl" />
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {/* --- NOT DÜZENLEME MODALI (GLASSMORPHISM) --- */}
            {isModalVisible && editingAppointment && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/60 backdrop-blur-sm p-4 animate-fadeIn" onClick={() => !noteSaving && setIsModalVisible(false)}>
                    <div className="w-full max-w-md bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl overflow-hidden transform transition-all" onClick={e => e.stopPropagation()}>

                        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/50">
                            <h3 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2">
                                <MdEditNote className="text-brand-500 text-2xl" /> Randevu Notu
                            </h3>
                            <button onClick={() => !noteSaving && setIsModalVisible(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                                <MdClose className="text-2xl" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="flex flex-col gap-1 mb-5 p-3 rounded-xl bg-gray-50 dark:bg-navy-900/50 border border-gray-100 dark:border-white/5">
                                <p className="text-sm text-gray-500 dark:text-gray-400"><strong className="text-navy-700 dark:text-white">Müşteri:</strong> {editingAppointment.customer?.name} {editingAppointment.customer?.surname}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400"><strong className="text-navy-700 dark:text-white">Hizmet:</strong> {editingAppointment.service?.name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400"><strong className="text-navy-700 dark:text-white">Zaman:</strong> {formatDateWithDay(editingAppointment.appointment_date)} | {editingAppointment.appointment_time?.substring(0,5)}</p>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase mb-2 block ml-1">Not İçeriği</label>
                                <textarea
                                    rows={4}
                                    value={currentNote}
                                    onChange={(e) => setCurrentNote(e.target.value)}
                                    placeholder="Bu randevuyla ilgili özel bir not ekleyin..."
                                    className="w-full p-4 rounded-xl border border-gray-200 bg-white text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all resize-none shadow-sm"
                                />
                            </div>
                        </div>

                        <div className="p-5 pt-0 flex justify-end gap-3">
                            <button
                                onClick={() => setIsModalVisible(false)}
                                disabled={noteSaving}
                                className="px-6 py-2.5 rounded-xl bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-white text-sm font-bold hover:bg-gray-200 dark:hover:bg-navy-600 transition-colors disabled:opacity-50"
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleSaveNote}
                                disabled={noteSaving}
                                className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-bold shadow-lg shadow-brand-500/30 hover:bg-brand-600 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {noteSaving ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Kaydet'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        
        .custom-sidebar-scroll::-webkit-scrollbar { height: 4px; }
        .custom-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(163, 174, 208, 0.3); border-radius: 10px; }
        .dark .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); }
      `}</style>
        </div>
    );
};

export default StaffUpcomingAppointments;